import { Route } from 'react-router-dom'
import type { AuthSession } from '../features/auth/types'
import { featureFlags } from '../config/featureFlags'
import ProtectedRouteGuard from '../components/ProtectedRouteGuard'
import type { PermissionKey } from '../types/authorization'
import type { ProtectedRouteDescriptor, RouteAccess, RouteVisibility } from './types'

export function hasRouteAccess(userPermissions: Set<PermissionKey>, access?: RouteAccess) {
  const anyOf = access?.anyOf ?? []
  const allOf = access?.allOf ?? []
  const noneOf = access?.noneOf ?? []
  const passesAny = anyOf.length === 0 || anyOf.some((perm) => userPermissions.has(perm))
  const passesAll = allOf.length === 0 || allOf.every((perm) => userPermissions.has(perm))
  const passesNone = noneOf.every((perm) => !userPermissions.has(perm))
  return passesAny && passesAll && passesNone
}

export function hasRouteVisibility(session: AuthSession, visibility?: RouteVisibility) {
  if (!visibility) return true

  if (visibility.when && !visibility.when(session)) return false

  if (visibility.requiresClient && !session.currentClientId) return false

  const clientFlags = visibility.clientFlags
  if (clientFlags) {
    const showAdvisoryMapping = clientFlags.showAdvisoryMapping
    if (
      typeof showAdvisoryMapping === 'boolean' &&
      typeof session.currentClientShowAdvisoryMapping === 'boolean' &&
      session.currentClientShowAdvisoryMapping !== showAdvisoryMapping
    ) {
      return false
    }

    const showProjectStatus = clientFlags.showProjectStatus
    if (
      typeof showProjectStatus === 'boolean' &&
      typeof session.currentClientShowProjectStatus === 'boolean' &&
      session.currentClientShowProjectStatus !== showProjectStatus
    ) {
      return false
    }
  }

  const envFlags = visibility.envFlags
  if (envFlags) {
    if (
      typeof envFlags.enableAdvisoryAIMenu === 'boolean' &&
      featureFlags.enableAdvisoryAIMenu !== envFlags.enableAdvisoryAIMenu
    ) {
      return false
    }

    if (typeof envFlags.enableBasMenu === 'boolean' && featureFlags.enableBasMenu !== envFlags.enableBasMenu) {
      return false
    }
  }

  if (visibility.platformTypes?.length) {
    const sessionPlatformTypes = session.platformType || []
    const hasMatchingPlatform = visibility.platformTypes.some((typeId) => sessionPlatformTypes.includes(typeId))
    if (!hasMatchingPlatform) return false
  }

  return true
}

export function resolveRoutePath(path: string, session: AuthSession) {
  if (!path.includes(':clientId')) return path
  if (!session.currentClientId) return null
  return path.replace(':clientId', session.currentClientId)
}

export function renderProtectedRoute(descriptor: ProtectedRouteDescriptor) {
  return <Route key={descriptor.key} path={descriptor.path} element={<ProtectedRouteGuard descriptor={descriptor} />} />
}
